/**
 * A categorical bar series with period labels.
 *
 * This is the chart for "four quarters of revenue", "five years of filings",
 * "six months of listings" — a small, countable set of named periods where the
 * comparison between the bars IS the finding. It is deliberately not a
 * time-series curve: a curve implies continuity between its points, and four
 * quarters are four measurements, not a line.
 *
 * The paired bar is what makes it useful beyond one number per period. A solid
 * bar and its hatched twin (actual against estimate, revenue against profit,
 * asking against sold) sit inside one period slot, so the pairing is read as
 * one period's two readings rather than as eight unrelated columns.
 *
 * The plot carries NO `viewBox`, and that is the one deliberate departure in
 * this file. A `viewBox` with `preserveAspectRatio="none"` — which is how every
 * other chart in the kit fills its column — shears the coordinate system
 * horizontally, and a sheared hatch pattern arrives at a different angle and a
 * different density in every column width it is dropped into. Without a
 * viewBox the user space IS CSS pixels: the vertical axis is drawn in pixels
 * against a known plot height, the horizontal axis in percentages of whatever
 * the container turns out to be, and the hatch holds its 45° at every width.
 * The rule the departure serves — no chart takes a pixel WIDTH — is intact.
 */
"use client";
import { useId, type ReactElement, type ReactNode } from "react";
import {
  CRC_DIVIDER,
  CRC_STACK_GAP,
  CRC_TONE_COLOR,
  CRC_TRUNCATE_STYLE,
  CRC_TYPE_STYLE,
} from "./cresultcard.tokens";
import {
  CresultcardStackDensity,
  CresultcardTextStep,
} from "./cresultcard.types";
import type { CresultcardTone } from "./cresultcard.types";
import { useCresultcardViewport } from "./cresultcard.viewport";

const PLOT_HEIGHT = 104;
const PLOT_HEIGHT_MOBILE = 84;
/** Headroom, so the tallest bar reads as a peak rather than as a clipped edge. */
const PLOT_TOP_INSET = 5;
const BASELINE_WIDTH = 1;
/** The share of a period slot the bars occupy; the rest is the gutter. */
const BAR_SLOT_FILL = 0.62;
/** The gap between a solid bar and its hatched twin, as a share of the pair. */
const PAIR_GAP_RATIO = 0.16;
const BAR_RADIUS = 2;
const BAR_MUTED_OPACITY = 0.38;
const HATCH_TILE = 4;
const HATCH_STROKE = 1.4;
const HATCH_ROTATION = "rotate(45)";
const HATCH_BODY_OPACITY = 0.16;
/**
 * The label row carries NO gap: a gap would make each label's centre drift off
 * its own bar's centre by half a gutter, which is exactly the misattribution
 * the half-column inset exists to prevent on the area chart. The breathing room
 * is padding INSIDE each equal-width cell instead.
 */
const LABEL_INSET = 3;
const PERCENT = 100;
const GEOMETRY_PRECISION = 3;
/** `useId` emits colons, which are not legal inside a `url(#…)` reference. */
const ID_UNSAFE = /:/g;

/** One period's reading, and optionally the second reading it is paired with. */
export interface CresultcardBarSpec {
  readonly label: string;
  readonly value: number;
  readonly tone: CresultcardTone;
  /** A period the reader is not being asked to compare — context, not finding. */
  readonly muted: boolean;
  /** Drawn as the hatched twin inside the same period slot. */
  readonly paired?: number;
}

interface BarSlot {
  readonly x: number;
  readonly width: number;
}

interface BarSlotInput {
  readonly index: number;
  readonly count: number;
  readonly paired: boolean;
}

/** The solid bar's slot. A paired slot splits into two, with a gutter between. */
function solidSlot({ index, count, paired }: BarSlotInput): BarSlot {
  const slot = PERCENT / count;
  const inner = slot * BAR_SLOT_FILL;
  const start = index * slot + (slot - inner) / 2;
  if (!paired) {
    return { x: start, width: inner };
  }
  const gap = inner * PAIR_GAP_RATIO;
  return { x: start, width: (inner - gap) / 2 };
}

function hatchSlot(input: BarSlotInput): BarSlot {
  const solid = solidSlot(input);
  const slot = PERCENT / input.count;
  const inner = slot * BAR_SLOT_FILL;
  const gap = inner * PAIR_GAP_RATIO;
  return { x: solid.x + solid.width + gap, width: solid.width };
}

function percentOf(value: number): string {
  return `${value.toFixed(GEOMETRY_PRECISION)}%`;
}

interface BarRectProps {
  readonly slot: BarSlot;
  readonly share: number;
  readonly plotHeight: number;
  readonly fill: string;
  readonly opacity: number;
}

function CresultcardBarRect({
  slot,
  share,
  plotHeight,
  fill,
  opacity,
}: BarRectProps): ReactElement {
  const available = plotHeight - PLOT_TOP_INSET - BASELINE_WIDTH;
  const height = Math.max(share, 0) * available;
  return (
    <rect
      data-el="Cresultcard/BarRect/Root"
      fill={fill}
      height={height}
      opacity={opacity}
      rx={BAR_RADIUS}
      width={percentOf(slot.width)}
      x={percentOf(slot.x)}
      y={plotHeight - BASELINE_WIDTH - height}
    />
  );
}

interface CresultcardBarSeriesProps {
  readonly bars: readonly CresultcardBarSpec[];
  /** The timeframe control that decides what the bars ARE. Rendered as given. */
  readonly period?: ReactNode;
  /** The series' accessible name — the bars themselves are `aria-hidden`. */
  readonly label: string;
}

export function CresultcardBarSeries({
  bars,
  period,
  label,
}: CresultcardBarSeriesProps): ReactElement {
  const { isMobile } = useCresultcardViewport();
  const hatchId = useId().replace(ID_UNSAFE, "");
  const plotHeight = isMobile ? PLOT_HEIGHT_MOBILE : PLOT_HEIGHT;
  const count = bars.length;
  const peak = bars.reduce(
    (highest, bar): number =>
      Math.max(highest, bar.value, bar.paired ?? 0),
    0,
  );
  const shareOf = (value: number): number => (peak === 0 ? 0 : value / peak);

  return (
    <div
      data-el="Cresultcard/BarSeries/Root"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: CRC_STACK_GAP[CresultcardStackDensity.Tight],
        width: "100%",
        minWidth: 0,
      }}
    >
      {period != null && (
        <div
          data-el="Cresultcard/BarSeries/Period"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "end",
            minWidth: 0,
          }}
        >
          {period}
        </div>
      )}

      <svg
        aria-label={label}
        role="img"
        style={{ display: "block", width: "100%", height: plotHeight }}
      >
        <defs
        >
          {bars.map((bar, index): ReactElement | null =>
            bar.paired == null ? null : (
              <pattern
                height={HATCH_TILE}
                id={`${hatchId}-${String(index)}`}
                key={`${bar.label}-${String(index)}`}
                patternTransform={HATCH_ROTATION}
                patternUnits="userSpaceOnUse"
                width={HATCH_TILE}
              >
                <rect
                  fill={CRC_TONE_COLOR[bar.tone]}
                  height={HATCH_TILE}
                  opacity={HATCH_BODY_OPACITY}
                  width={HATCH_TILE}
                  x={0}
                  y={0}
                />
                <line
                  stroke={CRC_TONE_COLOR[bar.tone]}
                  strokeWidth={HATCH_STROKE}
                  x1={0}
                  x2={0}
                  y1={0}
                  y2={HATCH_TILE}
                />
              </pattern>
            ),
          )}
        </defs>

        {bars.map((bar, index): ReactElement => {
          const paired = bar.paired != null;
          const opacity = bar.muted ? BAR_MUTED_OPACITY : 1;
          return (
            <g
              data-el="Cresultcard/BarSeries/Entry"
              key={`${bar.label}-${String(index)}`}
            >
              <CresultcardBarRect
                fill={CRC_TONE_COLOR[bar.tone]}
                opacity={opacity}
                plotHeight={plotHeight}
                share={shareOf(bar.value)}
                slot={solidSlot({ index, count, paired })}
              />
              {bar.paired != null && (
                <CresultcardBarRect
                  fill={`url(#${hatchId}-${String(index)})`}
                  opacity={opacity}
                  plotHeight={plotHeight}
                  share={shareOf(bar.paired)}
                  slot={hatchSlot({ index, count, paired })}
                />
              )}
            </g>
          );
        })}

        <line
          stroke={CRC_DIVIDER}
          strokeWidth={BASELINE_WIDTH}
          x1="0%"
          x2="100%"
          y1={plotHeight - BASELINE_WIDTH / 2}
          y2={plotHeight - BASELINE_WIDTH / 2}
        />
      </svg>

      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 0,
          width: "100%",
          minWidth: 0,
        }}
      >
        {bars.map((bar, index): ReactElement => (
          <span
            key={`${bar.label}-${String(index)}`}
            style={{
              ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
              ...CRC_TRUNCATE_STYLE,
              boxSizing: "border-box",
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: 0,
              paddingInline: LABEL_INSET,
              opacity: bar.muted ? BAR_MUTED_OPACITY : 1,
              textAlign: "center",
            }}
          >
            {bar.label}
          </span>
        ))}
      </div>
    </div>
  );
}
